import { useState, useEffect, useRef } from 'react';
import { X, CircleAlert } from 'lucide-react';
import type { TBAccount, Engagement, Entity, JournalEntryHeader } from '../types';
import {
  db,
  validateNoDuplicateAccountNumber,
  createJournalEntryHeader,
} from '../db/database';
import { dollarsToCents } from '../utils/format';
import {
  DEFAULT_GROUPING,
  DEFAULT_SUBGROUPING,
  FORM_1120_GROUPINGS,
  FS_REF_LIST,
  allowsSubGrouping
} from '../utils/mappings';
import SubGroupingCombobox from './SubGroupingCombobox';

interface AccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  engagement: Engagement;
  entities?: Entity[];
  accountToEdit?: TBAccount | null;
  mAdjHeaders?: JournalEntryHeader[];
}

interface FormData {
  entity: string;
  accountNumber: string;
  accountName: string;
  bookBalance: string; // Keep as string for input, convert to cents on save
  grouping: string;
  subGrouping: string;
  mAdjRef: string;
}

interface ValidationErrors {
  entity?: string;
  accountNumber?: string;
  accountName?: string;
  bookBalance?: string;
  grouping?: string;
  subGrouping?: string;
}

export default function AccountModal({
  isOpen,
  onClose,
  onSave,
  engagement,
  entities = [],
  accountToEdit,
  mAdjHeaders = []
}: AccountModalProps) {
  const [formData, setFormData] = useState<FormData>({
    entity: entities[0]?.name ?? engagement.clientName,
    accountNumber: '',
    accountName: '',
    bookBalance: '0',
    grouping: DEFAULT_GROUPING,
    subGrouping: DEFAULT_SUBGROUPING,
    mAdjRef: ''
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldShake, setShouldShake] = useState(false);


  const isEditMode = !!accountToEdit;
  const accountNumberRef = useRef<HTMLInputElement>(null);

  // Initialize form data when modal opens or accountToEdit changes
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => accountNumberRef.current?.focus(), 0);
      if (accountToEdit) {
        // Edit mode - populate with existing data
        setFormData({
          entity: accountToEdit.entity,
          accountNumber: accountToEdit.accountNumber,
          accountName: accountToEdit.accountName,
          bookBalance: (accountToEdit.bookBalance / 100).toFixed(2),
          grouping: accountToEdit.grouping,
          subGrouping: accountToEdit.subGrouping || '',
          mAdjRef: accountToEdit.mAdjRef ?? ''
        });
      } else {
        // Add mode - reset to defaults
        setFormData({
          entity: entities[0]?.name ?? engagement.clientName,
          accountNumber: '',
          accountName: '',
          bookBalance: '0',
          grouping: DEFAULT_GROUPING,
          subGrouping: DEFAULT_SUBGROUPING,
          mAdjRef: ''
        });
      }
      setErrors({});
      setShouldShake(false);
    }
  }, [isOpen, accountToEdit, engagement.clientName, entities]);

  // Auto-clear SubGrouping if Grouping changes to one that doesn't allow it
  useEffect(() => {
    if (!allowsSubGrouping(formData.grouping)) {
      setFormData(prev => ({ ...prev, subGrouping: '' }));
    }
  }, [formData.grouping]);

  const validateForm = async (): Promise<boolean> => {
    const newErrors: ValidationErrors = {};

    // Entity validation
    if (!formData.entity.trim()) {
      newErrors.entity = 'Entity is required';
    }

    // Account Number validation
    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = 'Account Number is required';
    } else {
      // TB-001: Check for duplicate account numbers
      const isDuplicate = !(await validateNoDuplicateAccountNumber(
        engagement.id!,
        formData.accountNumber,
        accountToEdit?.id
      ));
      if (isDuplicate) {
        newErrors.accountNumber = 'Account Number already exists in this engagement';
      }
    }

    // Account Name validation
    if (!formData.accountName.trim()) {
      newErrors.accountName = 'Account Name is required';
    }

    // Book Balance validation
    const bookBalanceNum = parseFloat(formData.bookBalance);
    if (isNaN(bookBalanceNum)) {
      newErrors.bookBalance = 'Book Balance must be a valid number';
    } else {
      // Check decimal places
      const decimalPart = formData.bookBalance.split('.')[1];
      if (decimalPart && decimalPart.length > 2) {
        newErrors.bookBalance = 'Book Balance cannot have more than 2 decimal places';
      }
    }

    // Grouping validation
    if (!formData.grouping) {
      newErrors.grouping = 'Grouping is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    const isValid = await validateForm();
    
    if (!isValid) {
      // Trigger shake animation
      setShouldShake(true);
      setTimeout(() => setShouldShake(false), 500);
      return;
    }

    setIsSubmitting(true);

    try {
      const bookBalanceCents = dollarsToCents(parseFloat(formData.bookBalance));
      const now = Date.now();

      const accountData: TBAccount = {
        id: accountToEdit?.id || crypto.randomUUID(),
        engagementId: engagement.id!,
        entity: formData.entity,
        accountNumber: formData.accountNumber,
        accountName: formData.accountName,
        bookBalance: bookBalanceCents,
        rje: accountToEdit?.rje || 0,
        aje: accountToEdit?.aje || 0,
        mAdj: accountToEdit?.mAdj || 0,
        pyAdjBook: accountToEdit?.pyAdjBook || 0,
        pyMAdj: accountToEdit?.pyMAdj || 0,
        pyFtax: accountToEdit?.pyFtax || 0,
        pyBalance: accountToEdit?.pyBalance || 0,
        grouping: formData.grouping,
        subGrouping: formData.subGrouping || undefined,
        createdAt: accountToEdit?.createdAt || now,
        updatedAt: now
      };

      if (isEditMode) {
        await db.accounts.put(accountData);
      } else {
        await db.accounts.add(accountData);
      }

      // Auto-create a Depreciation M-Adjustment header if one doesn't exist yet
      if (
        formData.grouping === 'Depreciation' &&
        !mAdjHeaders.some(h => /depreciation/i.test(h.jeName))
      ) {
        await createJournalEntryHeader({
          engagementId: engagement.id!,
          jeNumber: `M-${mAdjHeaders.length + 1}`,
          jeName: 'Depreciation',
          isRJE: false,
          totalDebits: 0,
          signoffState: null,
          notes: '',
          netAdjustmentCents: 0,
          updatedAt: Date.now(),
          differenceType: 'Temporary',
          calcType: 'IncomeStatement',
          calcInputs: {},
        });
      }

      onSave(); // Notify parent to refresh data
      onClose();
    } catch (error) {
      console.error('Error saving account:', error);
      alert('An error occurred while saving the account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData({ ...formData, [field]: value });
    // Clear error for this field when user starts typing
    if (errors[field as keyof ValidationErrors]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  if (!isOpen || !engagement.id) return null;

  const showSubGrouping = allowsSubGrouping(formData.grouping);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm">
      <div
        className={`glass-panel rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto transition-colors duration-300 ${
          shouldShake ? 'animate-shake' : ''
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-800">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
              {isEditMode ? 'Edit Account' : 'New Account'}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {engagement.clientName}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5 text-taxtrail-red dark:text-red-400" />
          </button>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-4">
          {/* Entity */}
          {entities.length > 1 && (
            <div>
              <label className="block text-sm font-medium mb-1 text-slate-900 dark:text-slate-100">
                Entity<span className="text-taxtrail-red dark:text-red-400">*</span>
              </label>
              <select
                value={formData.entity}
                onChange={(e) => handleInputChange('entity', e.target.value)}
                disabled={isSubmitting}
                className={`w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-colors duration-200 ${
                  errors.entity
                    ? 'border border-taxtrail-red dark:border-red-400'
                    : 'border border-slate-200 dark:border-slate-700'
                }`}
              >
                {entities.map(e => (
                  <option key={e.id} value={e.name}>{e.name}</option>
                ))}
              </select>
              {errors.entity && (
                <p className="text-xs text-taxtrail-red dark:text-red-400 mt-1 flex items-center gap-1">
                  <CircleAlert className="w-3 h-3" />
                  {errors.entity}
                </p>
              )}
            </div>
          )}

          {/* Two-column primary fields */}
          <div className="grid grid-cols-2 gap-4">
            {/* Left: Account Number + Account Name */}
            <div className="space-y-4">
              {/* Account Number */}
              <div>
                <label className="block text-sm font-medium mb-1 text-slate-900 dark:text-slate-100">
                  Account Number<span className="text-taxtrail-red dark:text-red-400">*</span>
                </label>
                <input
                  ref={accountNumberRef}
                  type="text"
                  value={formData.accountNumber}
                  onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                  disabled={isSubmitting}
                  className={`w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-colors duration-200 ${
                    errors.accountNumber
                      ? 'border border-taxtrail-red dark:border-red-400'
                      : 'border border-slate-200 dark:border-slate-700'
                  }`}
                  placeholder="Enter account number"
                />
                {errors.accountNumber && (
                  <p className="text-xs text-taxtrail-red dark:text-red-400 mt-1 flex items-center gap-1">
                    <CircleAlert className="w-3 h-3" />
                    {errors.accountNumber}
                  </p>
                )}
              </div>

              {/* Account Name */}
              <div>
                <label className="block text-sm font-medium mb-1 text-slate-900 dark:text-slate-100">
                  Account Name<span className="text-taxtrail-red dark:text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.accountName}
                  onChange={(e) => handleInputChange('accountName', e.target.value)}
                  disabled={isSubmitting}
                  className={`w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-colors duration-200 ${
                    errors.accountName
                      ? 'border border-taxtrail-red dark:border-red-400'
                      : 'border border-slate-200 dark:border-slate-700'
                  }`}
                  placeholder="Enter account name"
                />
                {errors.accountName && (
                  <p className="text-xs text-taxtrail-red dark:text-red-400 mt-1 flex items-center gap-1">
                    <CircleAlert className="w-3 h-3" />
                    {errors.accountName}
                  </p>
                )}
              </div>
            </div>

            {/* Right: Grouping + SubGrouping */}
            <div className="space-y-4">
              {/* Grouping */}
              <div>
                <label className="block text-sm font-medium mb-1 text-slate-900 dark:text-slate-100">
                  Grouping<span className="text-taxtrail-red dark:text-red-400">*</span>
                </label>
                <select
                  value={formData.grouping}
                  onChange={(e) => handleInputChange('grouping', e.target.value)}
                  disabled={isSubmitting}
                  className={`w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-colors duration-200 ${
                    errors.grouping
                      ? 'border border-taxtrail-red dark:border-red-400'
                      : 'border border-slate-200 dark:border-slate-700'
                  }`}
                >
                  {FS_REF_LIST.map((fsRef) => (
                    <optgroup key={fsRef} label={fsRef} className="font-bold text-charcoal-800 dark:text-charcoal-100">
                      {(FORM_1120_GROUPINGS[fsRef] || []).map((groupName) => (
                        <option key={groupName} value={groupName} className="font-normal pl-4">
                          {groupName}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {errors.grouping && (
                  <p className="text-xs text-taxtrail-red dark:text-red-400 mt-1 flex items-center gap-1">
                    <CircleAlert className="w-3 h-3" />
                    {errors.grouping}
                  </p>
                )}
              </div>

              {/* SubGrouping — Searchable Combobox (conditionally visible) */}
              {showSubGrouping && (
                <div>
                  <label className="block text-sm font-medium mb-1 text-slate-900 dark:text-slate-100">
                    SubGrouping
                  </label>
                  <SubGroupingCombobox
                    engagementId={engagement.id!}
                    value={formData.subGrouping}
                    onChange={(val) => handleInputChange('subGrouping', val)}
                    disabled={isSubmitting}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Full-width secondary section: Book Balance + M-Adj Reference */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            {/* Book Balance */}
            <div>
              <label className="block text-sm font-medium mb-1 text-slate-900 dark:text-slate-100">
                Book Balance<span className="text-taxtrail-red dark:text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.bookBalance}
                onChange={(e) => handleInputChange('bookBalance', e.target.value)}
                disabled={isSubmitting}
                className={`w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-right transition-colors duration-200 ${
                  errors.bookBalance
                    ? 'border border-taxtrail-red dark:border-red-400'
                    : 'border border-slate-200 dark:border-slate-700'
                }`}
                placeholder="0.00"
              />
              {errors.bookBalance && (
                <p className="text-xs text-taxtrail-red dark:text-red-400 mt-1 flex items-center gap-1">
                  <CircleAlert className="w-3 h-3" />
                  {errors.bookBalance}
                </p>
              )}
            </div>

            {/* M-Adjustment Reference */}
            <div>
              <label className="block text-sm font-medium mb-1 text-slate-900 dark:text-slate-100">
                M-Adjustment Reference
              </label>
              <select
                value={formData.mAdjRef}
                onChange={(e) => handleInputChange('mAdjRef', e.target.value)}
                disabled={isSubmitting}
                className="w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 transition-colors duration-200"
              >
                <option value="">None</option>
                {mAdjHeaders.map((header) => (
                  <option key={header.id} value={header.jeNumber}>
                    {header.jeNumber}: {header.jeName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-200 dark:border-slate-800 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSubmitting}
            className="px-6 py-2 rounded-lg font-medium bg-sage-600 hover:bg-sage-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-sage-500 focus:ring-offset-2"
          >
            {isSubmitting ? 'Saving...' : 'SAVE'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
          20%, 40%, 60%, 80% { transform: translateX(8px); }
        }
        .animate-shake {
          animation: shake 0.5s;
        }
      `}</style>
    </div>
  );
}
